import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { 
  collection, onSnapshot, query, doc, addDoc, 
  updateDoc, deleteDoc, where, Timestamp, orderBy 
} from 'firebase/firestore';
import Swal from 'sweetalert2';

function Paymentmode() {
  const [modes, setModes] = useState([]);
  const [formData, setFormData] = useState({ name: '', type: 'Digital' });
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 7;

  const userId = auth.currentUser?.uid;

  const Toast = Swal.mixin({
    toast: true, position: 'top-end', showConfirmButton: false, timer: 3000,
    background: '#fff', color: '#333'
  });

  useEffect(() => {
    if (!userId) return;
    const q = query(collection(db, "payment_modes"), where("userId", "==", userId), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setModes(list);
    });
    return () => unsubscribe();
  }, [userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    setLoading(true);

    try {
      if (editId) {
        await updateDoc(doc(db, "payment_modes", editId), { ...formData, updatedAt: Timestamp.now() });
        Toast.fire({ icon: 'success', title: 'Updated Successfully' });
      } else {
        await addDoc(collection(db, "payment_modes"), { ...formData, userId, createdAt: Timestamp.now() });
        Toast.fire({ icon: 'success', title: 'New Mode Added' });
      }
      resetForm();
    } catch (err) {
      Toast.fire({ icon: 'error', title: 'Error saving data' });
    } finally { setLoading(false); }
  };

  const deleteMode = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This payment mode will be permanently removed.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#eb3b5a',
      cancelButtonColor: '#4b7bec',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      await deleteDoc(doc(db, "payment_modes", id));
      Toast.fire({ icon: 'success', title: 'Deleted' });
    }
  };

  const resetForm = () => {
    setFormData({ name: '', type: 'Digital' });
    setEditId(null);
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setFormData({ name: item.name, type: item.type });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredData = modes.filter(m => m.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentItems = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Helper for Category Badges
  const getBadgeClass = (type) => {
    switch(type) {
      case 'Cash': return 'bg-success';
      case 'Digital': return 'bg-primary';
      case 'Credit': return 'bg-danger';
      case 'Bank': return 'bg-info';
      default: return 'bg-secondary';
    }
  };

  return (
    <div className="content-wrapper" style={{ backgroundColor: '#f4f6f9' }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 text-bold" style={{ fontSize: '1.5rem', color: '#2c3e50' }}>Settings</h1>
              <p className="text-muted text-sm">Manage how your customers pay you</p>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            {/* Form Column */}
            <div className="col-lg-4">
              <div className="card shadow-sm border-0">
                <div className="card-header bg-white py-3">
                  <h3 className="card-title text-bold text-primary">
                    <i className={editId ? "fas fa-edit mr-2" : "fas fa-plus-circle mr-2"}></i>
                    {editId ? 'Modify Payment Mode' : 'Create New Mode'}
                  </h3>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="card-body">
                    <div className="form-group mb-4">
                      <label className="text-muted small text-uppercase font-weight-bold">Payment Name</label>
                      <input type="text" className="form-control form-control-border" 
                        style={{ fontSize: '0.9rem' }}
                        value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} 
                        placeholder="e.g. PhonePe QR / IDBI Bank" required />
                    </div>
                    <div className="form-group mb-2">
                      <label className="text-muted small text-uppercase font-weight-bold">Classification</label>
                      <select className="form-control custom-select" 
                        style={{ fontSize: '0.9rem' }}
                        value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})}>
                        <option value="Digital">Digital (UPI / Card)</option>
                        <option value="Cash">Physical Cash</option>
                        <option value="Credit">Credit (Outstanding)</option>
                        <option value="Bank">Direct Bank Transfer</option>
                      </select>
                    </div>
                  </div>
                  <div className="card-footer bg-white border-0 text-right pb-4">
                    <button type="submit" disabled={loading} className={`btn btn-flat px-4 ${editId ? 'btn-info' : 'btn-primary'}`}>
                       {loading ? 'Processing...' : editId ? 'Update Mode' : 'Save Mode'}
                    </button>
                    {editId && <button type="button" onClick={resetForm} className="btn btn-link text-muted btn-sm">Cancel</button>}
                  </div>
                </form>
              </div>
            </div>

            {/* List Column */}
            <div className="col-lg-8">
              <div className="card shadow-sm border-0">
                <div className="card-header bg-white py-3 d-flex align-items-center justify-content-between">
                  <h3 className="card-title text-bold mb-0">Existing Methods</h3>
                  <div className="input-group input-group-sm" style={{ width: '200px' }}>
                    <div className="input-group-prepend">
                      <span className="input-group-text bg-white border-right-0"><i className="fas fa-search text-muted"></i></span>
                    </div>
                    <input type="text" className="form-control border-left-0 shadow-none" placeholder="Filter list..." 
                      onChange={(e) => setSearchTerm(e.target.value)} />
                  </div>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover text-center m-0">
                      <thead style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                        <tr className="text-muted small text-uppercase">
                          <th style={{ width: '80px' }}>#</th>
                          <th className="text-left">Payment Identity</th>
                          <th>Category</th>
                          <th style={{ width: '120px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.length > 0 ? currentItems.map((m, index) => (
                          <tr key={m.id} style={{ transition: 'all 0.3s' }}>
                            <td className="align-middle text-muted">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                            <td className="align-middle text-left text-bold" style={{ fontSize: '0.95rem', color: '#34495e' }}>{m.name}</td>
                            <td className="align-middle">
                              <span className={`badge px-3 py-2 rounded-pill ${getBadgeClass(m.type)}`} style={{ fontSize: '0.7rem' }}>
                                {m.type}
                              </span>
                            </td>
                            <td className="align-middle">
                              <div className="btn-group">
                                <button onClick={() => startEdit(m)} className="btn btn-light btn-sm rounded-circle mr-2 shadow-sm" title="Edit">
                                  <i className="fas fa-pencil-alt text-primary"></i>
                                </button>
                                <button onClick={() => deleteMode(m.id)} className="btn btn-light btn-sm rounded-circle shadow-sm" title="Delete">
                                  <i className="fas fa-trash-alt text-danger"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        )) : (
                          <tr><td colSpan="4" className="py-5 text-muted">No records found matching your search.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                {/* Modern Pagination */}
                <div className="card-footer bg-white py-3 border-top">
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="text-xs text-muted">Showing {currentItems.length} of {filteredData.length} records</span>
                    <nav>
                      <ul className="pagination pagination-sm m-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link rounded-pill px-3" onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
                        </li>
                        {[...Array(totalPages)].map((_, i) => (
                          <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                            <button className="page-link mx-1 rounded-circle" style={{ width: '30px', height: '30px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button className="page-link rounded-pill px-3" onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Paymentmode;